import { Signal } from '@angular/core';
import { Role } from '@tourfold/libraries/tf-api-client-public-v2';
import { providePageContext } from '@tourfold/shared-kernels/page-context';

const MAX_VISIBLE_ROLES = 25;

export interface RolesAiPageContextSignals {
  readonly roles: Signal<Role[]>;
  readonly searchQuery: Signal<string>;
  readonly editingRole: Signal<Role | null>;
  readonly isEditorOpen: Signal<boolean>;
}

/**
 * Registers AI assistant page context for the roles tab of /settings/access.
 *
 * - `entity` = the role being edited (dialog open). Create dialog leaves
 *   `entity` undefined.
 * - `visible_rows` = first 25 roles by name.
 * - `filters` = search query.
 *
 * Coexists with `users-list.ai-page-context.ts` on the same Access route. Both
 * inner components are mounted by p-tabs; the active tab's signals drive
 * the most recent `setOverride` call (last write wins) — see frontend/AGENTS.md.
 */
export function provideRolesAiPageContext(state: RolesAiPageContextSignals): void {
  providePageContext(() => {
    const editing = state.isEditorOpen() ? state.editingRole() : null;
    const search = state.searchQuery().trim();

    return {
      entity: editing
        ? {
            type: 'role',
            id: editing.id,
            label: editing.name,
          }
        : undefined,
      visible_rows: state
        .roles()
        .slice(0, MAX_VISIBLE_ROLES)
        .map((role) => ({
          type: 'role',
          id: role.id,
          label: role.name,
        })),
      filters: {
        ...(search.length > 0 && { search }),
      },
    };
  });
}
